import React, { ChangeEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router';
import { useApptekster } from '../../contexts/ApptekstContext';
import {
    skiltbestillingsteg,
    useSkiltbestillingState,
} from './Skiltbestilling';
import { Stegveiviser } from '../../components/Stegveiviser';
import { SVVBreadcrumbLink } from '@komponentkassen/svv-breadcrumbs';
import { SVVLayoutBanner } from '@komponentkassen/svv-layout';
import { SVVRadioGroup, SVVTextInput } from '@komponentkassen/svv-form';
import {
    SVVButton,
    SVVButtonCancel,
    SVVButtonRow,
} from '@komponentkassen/svv-button';

interface VarslingData {
    varslingSMS: string;
    varslingEpost: string;
}

export default function Varsling() {
    const t = useApptekster();
    const navigate = useNavigate();
    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<VarslingData>();
    const [feilmelding, setFeilmelding] = useState(false);
    const rettighetId = useSkiltbestillingState((state) => state.rettighetId);
    const trafikkstasjon = useSkiltbestillingState(
        (state) => state.trafikkstasjon,
    );
    const varselmetode = useSkiltbestillingState((state) => state.varselmetode);
    const setVarselmetode = useSkiltbestillingState(
        (state) => state.setVarselmetode,
    );
    const varselKontaktinfo = useSkiltbestillingState(
        (state) => state.varselKontaktinfo,
    );
    const setVarselKontaktinfo = useSkiltbestillingState(
        (state) => state.setVarselKontaktinfo,
    );
    const [epostTekst, setEpostTekst] = useState(
        varselmetode === 'Epost' ? varselKontaktinfo : '',
    );
    const [smsTekst, setSMSTekst] = useState(
        varselmetode === 'SMS' ? varselKontaktinfo : '',
    );

    if (!trafikkstasjon) {
        return <Navigate to="/" />;
    }

    return (
        <>
            <SVVLayoutBanner>
                <SVVBreadcrumbLink
                    href="#/statusoversikt"
                    ariaLabel={t('personlig-kjennemerke.snarvei.status')}
                    className="intern-link"
                >
                    {t('personlig-kjennemerke.snarvei.status')}
                </SVVBreadcrumbLink>
                <h1 className="spacing-bottom--medium">
                    {t('personlig-kjennemerke.bestill.overskrift')}
                </h1>
            </SVVLayoutBanner>

            <SVVLayoutBanner secondary>
                <form
                    className="form-group"
                    onSubmit={handleSubmit(() => {
                        if (varselmetode && (smsTekst || epostTekst)) {
                            varselmetode === 'SMS'
                                ? setVarselKontaktinfo(smsTekst)
                                : setVarselKontaktinfo(epostTekst);

                            navigate('/skiltbestilling/send');
                        } else {
                            setFeilmelding(true);
                        }
                    })}
                    noValidate
                >
                    <div className="form-layout spacing-bottom--medium">
                        <div className="form-layout__nav">
                            <Stegveiviser
                                steg={skiltbestillingsteg}
                                aktivtSteg={4}
                            />
                        </div>
                        <div className="form-layout__main">
                            <h2 className="spacing-bottom--xsmall">
                                {t(
                                    'personlig-kjennemerke.bestill.steg.varsling',
                                )}
                            </h2>{' '}
                            <p className="responsive-spacing-bottom--small">
                                {t(
                                    'personlig-kjennemerke.bestill.varsling.ingress',
                                )}
                            </p>
                            <SVVRadioGroup
                                id="velg-varslingsmetode"
                                legend={t(
                                    'personlig-kjennemerke.bestill.varsling.hvordan',
                                )}
                                name="velg-varslingsmetode"
                                selected={varselmetode}
                                options={[
                                    {
                                        value: 'SMS',
                                        text: t(
                                            'personlig-kjennemerke.bestill.varsling.sms',
                                        ),
                                    },
                                    {
                                        value: 'Epost',
                                        text: t(
                                            'personlig-kjennemerke.bestill.varsling.epost',
                                        ),
                                    },
                                ]}
                                errorMessage={
                                    feilmelding
                                        ? t(
                                              'personlig-kjennemerke.bestill.steg.varsling.mangler-varselmetode',
                                          )
                                        : undefined
                                }
                                required
                                direction="vertical"
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>,
                                ) => {
                                    setFeilmelding(false);
                                    setVarselmetode(event.target.value);
                                }}
                            />
                            {varselmetode === 'SMS' && (
                                <SVVTextInput
                                    id={'input-sms'}
                                    label={t(
                                        'personlig-kjennemerke.bestill.varsling.sms.tekstboks-label',
                                    )}
                                    type={'tel'}
                                    inputMode={'numeric'}
                                    placeholder={t(
                                        'personlig-kjennemerke.bestill.varsling.sms.placeholder',
                                    )}
                                    value={smsTekst}
                                    maxLength={8}
                                    errorMessage={errors.varslingSMS?.message}
                                    {...register('varslingSMS', {
                                        required: t(
                                            'personlig-kjennemerke.bestill.varsling.sms.ugyldig',
                                        ),
                                        pattern: {
                                            value: /^\d{8}$/,
                                            message: t(
                                                'personlig-kjennemerke.bestill.varsling.sms.ugyldig',
                                            ),
                                        },
                                        onChange: (event) =>
                                            setSMSTekst(event.target.value),
                                    })}
                                    required
                                />
                            )}
                            {varselmetode === 'Epost' && (
                                <SVVTextInput
                                    id={'input-epost'}
                                    label={t(
                                        'personlig-kjennemerke.bestill.varsling.epost.tekstboks-label',
                                    )}
                                    type={'email'}
                                    placeholder={t(
                                        'personlig-kjennemerke.bestill.varsling.epost.placeholder',
                                    )}
                                    value={epostTekst}
                                    errorMessage={errors.varslingEpost?.message}
                                    {...register('varslingEpost', {
                                        required: t(
                                            'personlig-kjennemerke.bestill.varsling.epost.ugyldig',
                                        ),
                                        pattern: {
                                            value: /^[\w!#$%&'*+/=?`{|}~^-]+(?:\.[\w!#$%&'*+/=?`{|}~^-]+)*@(?:[A-Z0-9-]+\.)+[A-Z]{2,63}$/i,
                                            message: t(
                                                'personlig-kjennemerke.bestill.varsling.epost.ugyldig',
                                            ),
                                        },
                                        onChange: (event) =>
                                            setEpostTekst(event.target.value),
                                    })}
                                    required
                                />
                            )}
                            <div className="svv-knapperad--spacing-top">
                                <SVVButtonRow>
                                    <SVVButton type="submit">
                                        {t('personlig-kjennemerke.delt.neste')}
                                    </SVVButton>
                                    <SVVButton
                                        onClick={() => navigate(-1)}
                                        color="secondary"
                                    >
                                        {t(
                                            'personlig-kjennemerke.delt.tilbake',
                                        )}
                                    </SVVButton>
                                    <SVVButtonCancel
                                        href={`#status/${rettighetId}`}
                                    >
                                        {t('personlig-kjennemerke.delt.avbryt')}
                                    </SVVButtonCancel>
                                </SVVButtonRow>
                            </div>
                        </div>
                    </div>
                </form>
            </SVVLayoutBanner>
        </>
    );
}
